import React from 'react';
import PropTypes from 'prop-types';
import { Route } from 'react-router-dom';
import classNames from 'classnames';
import fr from 'facet-rest';
import { withStyles } from '@material-ui/core/styles';
import { Switch } from 'Components/UI/FacetRouter/FacetRouter';
import {
  adminRoot,
  advisorNotificationCenterPath,
  advisorPaths,
  advisorResourcesPath,
  investmentsUri,
  scheduledMessagesUri,
} from 'Consts/advisorUris';
import {
  accountRequestPath,
  advisorActivityFeedPath,
  advisorAssumptionsAdminPath,
  advisorBulkRolloverRecommendationQuestionnaires,
  advisorClientExperiencePath,
  advisorClientExperienceSavesPath,
  advisorHouseholdPath,
  advisorIdentityVerificationReviews,
  advisorLinkingAttemptsPath,
  advisorMessagesPath,
  advisorPageMessages,
  advisorTasksPath,
  advisorUserPath,
  featuresPath,
  meetingSummaryPath,
  preferencesPath,
} from 'Consts/Strings';
import BulkRolloverRecommendationQuestionnairePage
  from 'Components/AdvisorApp/Viewport/ApexAdminPortal/BulkRolloverRecommendationQuestionnaire';
import { Redirect } from 'react-router';
import UsersAndTeams from 'Components/AdvisorApp/Viewport/Users';
import FeatureToggles from 'Components/AdvisorApp/Viewport/FeatureToggles';
import Preferences from 'Components/AdvisorApp/Viewport/Preferences';
import AssumptionsAdmin from 'Components/AdvisorApp/Viewport/AssumptionsAdmin';
import SimpleActionItemTable from 'Components/UI/SimpleActionItemTable';
import useUserContext from 'Context/UserContext';
import relationships from 'Consts/Relationships';
import featureEnabled, { hasRelationship, pickComponentAndProps } from 'Helpers/Affordances';
import usePathIsMobileFeed from 'Hooks/usePathIsMobileFeed';
import ScheduledMessages from 'Components/UI/ScheduledMessages';
import ApexAdminPortal from 'Components/AdvisorApp/Viewport/ApexAdminPortal';
import MeetingSummary from 'Components/AdvisorApp/Viewport/MeetingSummary';
import MeetingSummaryTable from 'Components/UI/MeetingSummaryTable';
import ClientExperiencePicker from 'Components/AdvisorApp/Viewport/ClientExperienceSaves';
import CuratedContentArticles from 'Components/AdvisorApp/Viewport/CuratedContentArticles';
import IdentityVerificationReviews from 'Components/AdvisorApp/Viewport/ApexAdminPortal/IdentityVerificationReviews';
import LinkingAttempts from 'Components/AdvisorApp/Viewport/LinkingAttempts';
import { buildPageLink } from 'Views/Page/Content/Templates';
import { QueryVal } from 'Helpers/Query';
import { ModalProvider } from 'Views/Page/Contexts/ModalContext';
import Household from './Household';
import ResourceCenter from './ResourceCenter';
import NotificationCenter from './NotificationCenter';
import TodayDashboard from './TodayDashboard';
import Investments from './Investments';
import ClientExperience from './ClientExperience';

const styles = () => ({
  root: {
    display: 'flex',
    flexGrow: 1,
    width: '100%',
  },
  rootMobileFeed: {
    paddingLeft: '0px', // Removes viewport padding (sidebar is not visible for mobile feed)
  },
});

function Viewport({ classes }) {
  const { hasSuperUserRole, isInvestmentOps, agentResource } = useUserContext();
  const { component: TaskBoardComponent } = pickComponentAndProps(agentResource, {
    [relationships.default]: [SimpleActionItemTable, {}],
  });

  const { component: MeetingSummaryTableComponent } = pickComponentAndProps(agentResource, {
    [fr.relationships.meetingSummary.name]: [MeetingSummaryTable, {}],
    [fr.relationships.default.name]: null,
  });

  const pathIsMobileFeed = usePathIsMobileFeed();

  return (
    <ModalProvider>
      <div
        className={classNames(classes.root, {
          [classes.rootMobileFeed]: pathIsMobileFeed,
        })}
      >
        <Switch>
          {/* Redirect From old message paths to new messages page - still need this because of old emails */}
          {hasRelationship(agentResource, fr.relationships.messagesPage.name) && (
          <Redirect
            from={advisorMessagesPath}
            to={buildPageLink(agentResource.relationship(fr.relationships.messagesPage.name).link('related'), advisorPageMessages, { threadSelfLink: QueryVal('threadSelfLink', '') })}
          />
          )}
          {hasRelationship(agentResource, fr.relationships.messagesPage.name) && (
          <Redirect
            from={advisorActivityFeedPath}
            to={buildPageLink(agentResource.relationship(fr.relationships.messagesPage.name).link('related'), advisorPageMessages, { threadSelfLink: QueryVal('threadSelfLink', '') })}
          />
          )}
          <Route exact path={advisorTasksPath} component={TaskBoardComponent} />
          <Route path={advisorHouseholdPath} component={Household} />
          <Route
            exact
            path={advisorNotificationCenterPath}
            component={NotificationCenter}
          />
          {/*
            Only superusers are allowed to navigate to the user/team page.
          */}
          <Route
            exact
            path={advisorUserPath}
            render={() => {
              if (hasSuperUserRole) {
                return <UsersAndTeams />;
              }
              return <Redirect to={adminRoot} />;
            }}
          />
          <Route
            exact
            path={featuresPath}
            render={() => {
              if (hasSuperUserRole) {
                return <FeatureToggles />;
              }
              return <Redirect to={adminRoot} />;
            }}
          />
          <Route
            exact
            path={accountRequestPath}
            render={() => {
              return <ApexAdminPortal />;
            }}
          />
          <Route
            exact
            path={advisorBulkRolloverRecommendationQuestionnaires}
            render={() => {
              if (isInvestmentOps && featureEnabled(agentResource, fr.relationships.rolloverRecommendationQuestionnaire.name)) {
                return <BulkRolloverRecommendationQuestionnairePage />;
              }
              return <Redirect to={adminRoot} />;
            }}
          />
          <Route
            exact
            path={advisorIdentityVerificationReviews}
            render={() => {
              if (featureEnabled(agentResource, fr.relationships.identityVerificationReviews.name)) {
                return <IdentityVerificationReviews />;
              }
              return null;
            }}
          />
          <Route exact path={preferencesPath} component={Preferences} />
          <Route exact path={advisorAssumptionsAdminPath} component={AssumptionsAdmin} />
          <Route
            exact
            path={meetingSummaryPath}
            render={() => {
              if (MeetingSummaryTableComponent) {
                return <MeetingSummary />;
              }
              return null;
            }}
          />
          <Route exact path={advisorResourcesPath} component={ResourceCenter} />
          <Route
            exact
            path={investmentsUri}
            component={Investments}
          />
          <Route
            exact
            path={scheduledMessagesUri}
            component={ScheduledMessages}
          />
          <Route
            exact
            path={advisorClientExperienceSavesPath}
            component={ClientExperiencePicker}
          />
          <Route
            exact
            path={advisorLinkingAttemptsPath}
            component={LinkingAttempts}
          />
          <Route
            exact
            path={advisorClientExperiencePath}
            component={ClientExperience}
          />
          <Route
            exact
            path={advisorPaths.curatedContentArticles}
            component={CuratedContentArticles}
          />
          <Route
            render={() => {
              if (!agentResource) {
                return null;
              }

              return (
                <TodayDashboard />
              );
            }}
          />
        </Switch>
      </div>
    </ModalProvider>
  );
}

Viewport.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles, { name: 'Viewport' })(Viewport);
